import React, { FC, ReactNode, useEffect, useState } from "react"
import { CardContent, Grid, InputAdornment, InputLabel, TextField } from "@material-ui/core"
import {
	Today as ScheduledSnoozeIcon,
	Delete as CancelSnoozeIcon
} from "@material-ui/icons"

import { ActionDialog, Loading } from "@/components"
import MessageItem from "@/components/ChatMessageBuilder/MessageItem"

import { fullDatetime } from "@/utils/time"

import { Snooze } from "@/protocols/snooze"
import { WABAChannelMessageTemplate } from "@/@integrations/WABA/protocols/wabaChannelMessageTemplate"

import SnoozeService, { SnoozeType } from "@/services/Snooze"
import ErrorHandlerService from "@/services/ErrorHandler"
import WABAChannelMessageTemplateService from "@/@integrations/WABA/services/WABAChannelMessageTemplate"

import ScheduledSnoozeContentSkeleton from "@/skeletons/Admin/ScheduledSnoozeSkeleton"

import { ErrorType } from "@/hooks/useValidation"
import useSnooze from "@/hooks/useSnooze"

import WhatsappBackground from "@/assets/images/waba/whatsapp_background.png"

import useStyles from "@/pages/Attendance/Chat/ConversationPanel/ConversationHeader/NewSnooze/dialogs/ScheduledSnoozeDialog/styles"
import { BuildedMessage } from "@/protocols/messages"

type ScheduledSnoozeDialogProps = {
	snoozeId: number
	scheduleTime?: Date
	defaultOpen?: boolean
	inboxChannelId: number,
	inboxChannelChatId: number
}

const ScheduledSnoozeDialog: FC<ScheduledSnoozeDialogProps> = (props) => {
	const {
		snoozeId,
		scheduleTime,
		defaultOpen = false,
		inboxChannelId,
		inboxChannelChatId,
		children
	} = props

	const {
		handleCancelSnooze
	} = useSnooze({ inboxChannelChatId })

	const classes = useStyles()

	const [openScheduledSnoozeDialog, setOpenScheduledSnoozeDialog] = useState<boolean>(defaultOpen)

	const [snooze, setSnooze] = useState<Snooze | null>(null)
	const [loading, setLoading] = useState<boolean>(false)
	const [template, setTemplate] = useState<WABAChannelMessageTemplate | null>(null)

	const handleOpenScheduledSnoozeDialog = () => {
		setOpenScheduledSnoozeDialog(true)
	}

	const handleCloseScheduledSnoozeDialog = () => {
		setOpenScheduledSnoozeDialog(false)
	}

	const handleGetTemplate = async (wabaTemplateMessageId: number) => {
		try {
			const template = await WABAChannelMessageTemplateService.retrieveOneTemplateById(wabaTemplateMessageId, inboxChannelId)

			if (template) {
				setTemplate(template)
			}
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}
	}

	const handleGetSnooze = async () => {
		setLoading(true)

		try {
			const response = await SnoozeService.retrieveSnoozeById(snoozeId)

			if (response?.waba_channel_message_template_id) {
				await handleGetTemplate(response.waba_channel_message_template_id)
			}

			setSnooze(response)
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}

		setLoading(false)
	}

	const renderScheduleSnoozeContent = () => {
		const snoozeTypeToScheduledSnoozeContent: Record<SnoozeType, () => ReactNode> = {
			message: () => {
				const snoozeMessage = snooze?.content?.messages[0] as BuildedMessage

				return (
					<Grid container spacing={1} className={classes.messagePreview}>
						<Grid item xs={12}>
							<InputLabel>Mensagem:</InputLabel>
						</Grid>
						<Grid item xs={12}>
							<CardContent
								className={classes.previewMessageContent}
								style={{
									backgroundImage: `url(${WhatsappBackground})`
								}}
							>
								<MessageItem
									type={snoozeMessage?.type}
									content={snoozeMessage?.content}
									mediaName={snoozeMessage?.mediaName}
								/>
							</CardContent>
						</Grid>
					</Grid>
				)
			},
			"waba-message-template": () => {
				const snoozeTemplate = template as WABAChannelMessageTemplate

				return (
					<Grid container spacing={1} className={classes.messagePreview}>
						<Grid item xs={12}>
							<InputLabel>Template:</InputLabel>
						</Grid>
						<Grid item xs={12}>
							<CardContent
								className={classes.previewMessageContent}
								style={{
									backgroundImage: `url(${WhatsappBackground})`
								}}
							>
								<MessageItem
									type={snoozeTemplate?.message.type}
									content={snoozeTemplate.message.content}
									mediaName={snoozeTemplate.message?.mediaName}
									extraData={snoozeTemplate?.message?.extraData}
									isDefaultSize={false}
									copySpecial={false}
								/>
							</CardContent>
						</Grid>
					</Grid>
				)
			},
			default: () => null
		}

		const scheduledSnoozeContent = snoozeTypeToScheduledSnoozeContent[snooze?.type || "default"]

		if (scheduledSnoozeContent) {
			return (
				<Grid container>
					{scheduledSnoozeContent()}
				</Grid>
			)
		}
	}

	useEffect(() => {
		handleGetSnooze()
	}, [inboxChannelChatId, snoozeId])

	return (
		<Grid>
			<Grid onClick={handleOpenScheduledSnoozeDialog}>
				{children}
			</Grid>

			<ActionDialog
				title="Soneca"
				openDialog={openScheduledSnoozeDialog}
				maxWidth="md"
				onClose={handleCloseScheduledSnoozeDialog}
				onSave={handleCancelSnooze}
				saveText="Cancelar Soneca"
				cancelText="Fechar"
				saveButtonIcon={<CancelSnoozeIcon />}
				saveButtonProps={{
					className: classes.button
				}}
			>
				<Loading loading={loading} customLoadingElement={<ScheduledSnoozeContentSkeleton />}>
					<Grid container spacing={2} style={{ maxWidth: "400px" }}>
						<Grid item xs={12}>
							<Grid container spacing={2}>
								<Grid item xs={12}>
									<Grid container spacing={1}>
										<Grid item xs={12}>
											<InputLabel>Agendado para:</InputLabel>
										</Grid>
										<Grid item xs={12}>
											<TextField
												value={fullDatetime(new Date(scheduleTime as Date))}
												variant="outlined"
												type="text"
												fullWidth
												InputProps={{
													startAdornment: (
														<InputAdornment position="start">
															<ScheduledSnoozeIcon />
														</InputAdornment>
													),
													readOnly: true
												}}
											/>
										</Grid>
									</Grid>
								</Grid>
								<Grid item xs={12}>
									{renderScheduleSnoozeContent()}
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Loading>
			</ActionDialog>
		</Grid>
	)
}

export default ScheduledSnoozeDialog
